/* ============================================================================
   THE RISK MODEL, IN ONE PLACE                     (SITE-RULES 3b2)

   Every surface that scores a member calls this and nothing else: the Support Coach
   console, the Manager and Owner "Going quiet" panels, the member record's ring and its
   ladder panel. Until 2026-09-04 the console and the record each carried a copy of the
   arithmetic with different tier cut-offs and different rounding, so the same person was
   "80 · High risk" on the console and "81 · Amber" on her record, and the Manager and
   Owner ranked a third list by health entirely. On 2026-09-11 the model was ported to
   PHP so the live app could rank like the demo; within an hour it was a second source of
   truth that would have deducted 35 points from every member on a real tenant. It was
   reverted, and MIGRATION.md §2 is the scar. If you find yourself writing this
   arithmetic a second time, stop.

   THREE WEIGHTED FAMILIES, each normalised 0–1, then scaled:
       attendance 0.40 · anchors 0.35 · health 0.25
   The anchor carries nearly as much as attendance deliberately: it is the signal that
   predicts the other two.

   THE SCORE COUNTS DOWN FROM 100. 100 is a member who is training, known by name and
   holding their rhythm; every missing signal takes points off. So the LOW score is the
   one that needs attention and every ranked list sorts ASCENDING, worst first. It was
   inverted from a risk score on 2026-09-05 because the old number counted upward toward
   trouble and read backwards beside `health` (also 0–100, also high-is-good) on the same
   screen. It has been inverted once. Do not invert it again.

   TIER comes from the SCORE alone. THE STEP comes from the SITUATION: a member with
   no anchor is an invitation at any tier, because the other moves are a touch FROM an
   anchor and there is nobody to make it. See ladder.ts, which also records why the
   numbering came back after being removed on 2026-09-15.

   THE FAMILIES ARE THE DEDUCTIONS, so the record can show the arithmetic: 100, minus
   each family, equals the number in the ring.

   NEVER CALL THIS FOR A SUPPRESSED MEMBER (rule 1) or one inside their first 90 days
   (rule 3b). This function does not check; `lifecycleOf()` and the caller own that.
   ============================================================================ */
import { SCORE_AMBER, SCORE_RED } from './types.js';
import { nextStep } from './ladder.js';
import { scoreMember } from './scores.js';
import { todayISO } from './time.js';
/* ── ABSENCE NOW LIVES IN scores.ts ──────────────────────────────────────────
   The window, its multiples and its clamps moved there on 2026-09-18 when the five
   scores landed, and they are IMPORTED here rather than copied. Two copies of this
   arithmetic is exactly the defect MIGRATION.md §2 records: the same member read
   "80 · High risk" on one screen and "81 · Amber" on another because two files each
   held their own version of it. Re-exported so the existing callers keep working. */
export { ABSENCE_FREE_MULTIPLE, ABSENCE_NAMED_MULTIPLE, ABSENCE_FREE_MIN, ABSENCE_FREE_MAX, ABSENCE_NAMED_MIN, ABSENCE_NAMED_MAX, ABSENCE_FREE_DAYS, ABSENCE_NAMED_DAYS, ABSENCE_RAMP_DAYS, absenceWindow, normalGapDays, } from './scores.js';
/** Health below this is "slipping"; the ramp runs down to 40, which is the whole family. */
export const HEALTH_RAMP_TOP = 60;
export const HEALTH_RAMP_FLOOR = 40;
/** attendance · anchors · health. */
export const RISK_WEIGHTS_FULL = { absence: 0.40, anchor: 0.35, health: 0.25 };
/**
 * The same two signals, rescaled to sum to 1 with the anchor family removed:
 * 0.40/0.65 and 0.25/0.65. See `detectRiskModel`.
 */
export const RISK_WEIGHTS_NO_ANCHORS = { absence: 0.615, health: 0.385 };
/* NEITHER ANCHORS NOR HEALTH. Attendance carries the whole score, because it is the
   only thing the tenant actually has. FNS and The LAB are both this. */
export const RISK_WEIGHTS_ATTENDANCE = { absence: 1 };
/**
 * Which model this TENANT can honestly run. Ask it ONCE for the gym, not once per
 * member.
 *
 * A synced tenant (Mindbody gives attendance and billing; who knows whom comes from
 * nowhere) has no anchors on ANY member. Scored naively that is the heaviest single
 * penalty deducted from everybody, and `/dashboard` opens with the entire roster in Red
 * and "No anchor on record" beside every name — which is not a finding, it is the
 * product telling the truth about itself in the least useful possible way.
 * MIGRATION.md §9 calls this out as the trap waiting for every Acrostic-native concept.
 *
 * A roster where nobody has an anchor falls back to attendance + health. One anchor
 * anywhere means the gym IS modelling them, and the members without one are a real
 * finding. An empty roster answers 'full': there is nothing yet to be wrong about.
 */
export function detectRiskModel(members) {
    let seen = 0;
    let anyAnchor = false;
    let anyHealth = false;
    for (const m of members) {
        seen++;
        if (m.anchors && m.anchors.length)
            anyAnchor = true;
        if (m.health != null)
            anyHealth = true;
        /* Both found: nothing later can change the answer. */
        if (anyAnchor && anyHealth)
            return 'full';
    }
    if (seen === 0)
        return 'full';
    if (anyAnchor)
        return 'full';
    /* NO ANCHORS AND NO HEALTH. The trap this closes: `Number(m.health) || 0` read a
       figure nobody had recorded as a zero, which is the worst possible value, so the
       whole roster carried a penalty for a signal the gym does not collect. At FNS the
       ceiling was 62 against a Green threshold of 76 - not one member of 554 could
       reach Green whatever they did. The same reasoning the anchors fallback already
       used, one family over. */
    return anyHealth ? 'attendance+health' : 'attendance';
}
/** Presentation only: the design token family for a tier. Not part of RiskResult. */
export function tierClass(tier) {
    return tier === 'Red' ? 'risk' : tier === 'Amber' ? 'warn' : 'good';
}
export function riskModel(m, opts = {}) {
    const today = opts.today ?? todayISO();
    const model = opts.model ?? 'full';
    const modelsAnchors = model === 'full';
    const anchors = Array.isArray(m.anchors) ? m.anchors : [];
    /* ── THE FIVE SCORES DO THE ARITHMETIC NOW ───────────────────────────────
       Everything that used to be computed here - the absence ramp, the anchor
       states, the health ramp - moved into scores.ts on 2026-09-18 and is read back
       out. This function keeps its shape so that every caller, every stored cache
       and every screen carries on working, but it is a VIEW over the five now
       rather than a second model beside them. If you find yourself adding
       arithmetic below this line, it belongs in scores.ts. */
    const detail = scoreMember(m, {
        today,
        ...(opts.unsourced ? { unsourced: opts.unsourced } : {}),
    });
    /* THE FAMILIES ARE THE FIVE SCORES, expressed as deductions so the record can
       still show "100, minus each family, equals the number in the ring".
  
       The share is the honest one: the overall is the AVERAGE of the live scores, so
       each score's contribution to the total deduction is its own shortfall divided
       by how many are live. That makes the deductions sum to exactly 100 minus the
       overall, which is the property the ring is drawn from. */
    const live = detail.scores.filter((sc) => sc.value != null);
    const fam = live.map((sc) => ({
        label: sc.label,
        deducted: Math.round((100 - sc.value) / live.length),
    }));
    /* A SCORE THE GYM CANNOT ANSWER IS NOT A FAMILY SHOWING ZERO. "Community: 0"
       would read as "they bring nobody in", which is exactly the claim a gym that
       records no referrals cannot make. It is simply not in the list. */
    const score = detail.overall ?? 100;
    const deducted = 100 - score;
    const topDriver = fam.slice().sort((a, b) => b.deducted - a.deducted)[0]
        ?? { label: 'Nothing on record', deducted: 0 };
    /* THE WHY LINES COME FROM THE INPUTS THAT ARE DRAGGING, worst first, so the line
       beside a name is the arithmetic rather than a second opinion about it. */
    const why = live
        .flatMap((sc) => [...sc.inputs, ...(sc.pillars ?? []).flatMap((pl) => pl.inputs)])
        .filter((i) => i.why && i.value != null && i.value < 1)
        .sort((a, b) => (a.value - b.value) || (b.weight - a.weight))
        .map((i) => i.why)
        .slice(0, 4);
    const tier = score <= SCORE_RED ? 'Red' : score <= SCORE_AMBER ? 'Amber' : 'Green';
    const sev = tier === 'Red' ? 'high' : tier === 'Amber' ? 'med' : 'low';
    const { guidance, step } = theMove(tier, anchors.length > 0, modelsAnchors, m.unansweredOutreach);
    return { score, deducted, why, fam, topDriver, tier, sev, guidance, step, model, scores: detail };
}
/* ── The move ─────────────────────────────────────────────────────────────
   What to do about this person, and it follows the SITUATION rather than the number.
   Having nobody to make the touch outranks the tier: a warm message from "the club"
   is the thing the product exists to stop.

   THE NUMBERING IS BACK, AND THAT IS A REVERSAL. This was numbered once - Rung 1,
   2, 3 - and Anuj dropped the ladder on 2026-09-15 because staff read a rung as a
   rank, which is the one thing a care model must not be. PRD 4 reinstates it as a
   STEP with its denominator always attached, on the argument that "Step 3 of 5"
   reads as a position in the gym's own sequence while "Rung 3" read as a level of
   the member. The moves are the same moves. What changed is that they carry a
   number again, and that the number is only ever rendered beside the ACTION - see
   ladder.ts, which has no way to produce a bare one. */
function theMove(tier, hasAnchor, modelsAnchors, unansweredOutreach) {
    /* nextStep only needs to know WHETHER there is an anchor, not which; the caller
       has already resolved that from the real list. */
    const step = nextStep({
        tier,
        anchors: hasAnchor ? [PRESENT] : [],
        modelsAnchors,
        unansweredOutreach: unansweredOutreach ?? null,
    });
    /* The sentence stays the guidance, so every existing caller keeps working and a
       surface that has not been taught about steps still says what to do. */
    return {
        guidance: step ? step.move : 'No intervention. Do not manufacture contact.',
        step,
    };
}
/** A stand-in for "there is at least one", which is all nextStep reads. */
const PRESENT = { name: '', kind: 'staff', role: '', basis: '', since: '' };
/* cadenceLine and rhythmSuffix moved to scores.ts with the rest of the arithmetic.
   Re-exported, because the wording is product copy that other surfaces read. */
export { cadenceLine, rhythmSuffix } from './scores.js';
export { SCORE_AMBER, SCORE_RED };
